import { BranchGate } from "../../core/gates/branch-gate.js";
import { SpecValidator } from "../../core/sdd/validator.js";
import { HandoffEngine } from "../../core/handoff/handoff-engine.js";
import { runCollectEvidence } from "./collect-evidence.js";
import { QualityGuard } from "../../core/validation/quality-guard.js";
import { HealerEngine } from "../../core/healing/healer-engine.js";
import { createCliRemediationExecutor } from "../../core/healing/cli-remediation-executor.js";
import { isRecoverableGateFailure, isTerminalFailure } from "../../core/statuses.js";
import path from "node:path";
import fs from "node:fs/promises";
import { getWorkflowProfile, resolveWorkflowProfile } from "../../core/workflow-profiles.js";

function normalizeTaskSlugFromSpec(specPath: string): string {
  const normalized = String(specPath).replaceAll("\\", "/");
  const marker = "/docs/workflows/";
  const index = normalized.includes(marker)
    ? normalized.indexOf(marker) + marker.length
    : normalized.startsWith("docs/workflows/") ? "docs/workflows/".length : -1;
  if (index >= 0) {
    const slug = normalized.slice(index).split("/")[0];
    if (slug) return slug;
  }
  return path.basename(specPath, path.extname(specPath));
}

export function combineValidation(evidence: any, quality: any): any {
  const statuses = [evidence.internalStatus, quality.overallStatus];
  let overallStatus = "PASS";
  if (statuses.includes("BLOCKED")) overallStatus = "BLOCKED";
  else if (statuses.includes("FAIL")) overallStatus = "FAIL";
  else if (statuses.includes("FAIL_DELEGATION_GATE")) overallStatus = "FAIL_DELEGATION_GATE";
  else if (statuses.includes("FAIL_QUALITY_GATE")) overallStatus = "FAIL_QUALITY_GATE";
  else if (statuses.includes("PASS_WITH_NOTES")) overallStatus = "PASS_WITH_NOTES";
  return { overallStatus, evidence, quality };
}

export interface runMasterOrchestratorOptions {
  cwd: string;
  specPath?: string;
  override?: string;
  remediationExecutor?: any;
}

/**
 * Coordinates branch safety, specification validation, observed validation, bounded remediation, and handoff.
 * Failed required validation can never be promoted to success.
 */
export async function runMasterOrchestrator({ cwd, specPath, override, remediationExecutor = null }: runMasterOrchestratorOptions): Promise<any> {
  console.log("\n[AI WORKFLOW] Starting evidence-backed orchestration...\n");

  const branchGate = new BranchGate({ memoryDir: path.join(cwd, ".ai-workflow"), cwd });
  const taskSlug = specPath ? normalizeTaskSlugFromSpec(specPath) : "implementation";
  const gateResult = branchGate.check(override, { taskSlug });
  if (gateResult.blocked) throw new Error(`[GATE BLOCKED] ${gateResult.reason}`);
  console.log(`[PASS] Branch Gate: ${gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : `${gateResult.branch} is authorized`}.`);

  if (!specPath) throw new Error("[SDD BLOCKED] Missing --spec-path. An approved specification is required.");

  const validator = new SpecValidator();
  const absoluteSpecPath = path.isAbsolute(specPath) ? specPath : path.join(cwd, specPath);
  const validation = await validator.validate(absoluteSpecPath);
  if (!validation.valid) throw new Error(`[SDD BLOCKED] Specification is not ready: ${validation.reason}`);

  const specTier = validation.tier || "standard";
  const riskLevel = specTier === "deep" ? "high" : specTier === "tiny" ? "low" : "medium";
  const evidencePolicy = specTier === "deep" ? "required" : "optional";
  const remediationLimit = specTier === "deep" ? 3 : specTier === "tiny" ? 1 : 2;

  const specContent = await fs.readFile(absoluteSpecPath, "utf8");
  const workflowProfile = resolveWorkflowProfile({ request: specContent });
  const profileDefinition = getWorkflowProfile(workflowProfile);
  console.log(`[PASS] Specification: ${specTier.toUpperCase()} spec is APPROVED.`);
  console.log(`[PROFILE] ${workflowProfile} -> ${profileDefinition.owner}; skills: ${profileDefinition.skills.join(", ") || "none"}.`);

  const validateWorkflow = async () => {
    const evidence = await runCollectEvidence({
      cwd,
      exitOnError: false,
      taskSlug,
      evidencePolicy,
      profile: workflowProfile,
      branchRecovery: gateResult.recovered ? `${gateResult.branchBefore} -> ${gateResult.branch}` : "NOT_REQUIRED"
    });
    const quality = await new QualityGuard({ cwd, taskSlug, evidencePolicy, riskLevel }).verify();
    return combineValidation(evidence, quality);
  };

  let result = await validateWorkflow();
  if (isRecoverableGateFailure(result.overallStatus)) {
    console.log(`\n[REMEDIATION REQUIRED] ${result.overallStatus}. Starting bounded remediation (limit: ${remediationLimit}).`);
    // Use the provided executor (programmatic API) or fall back to the default CLI executor.
    // The CLI executor re-runs failed npm scripts; it does not modify source code.
    const executor = typeof remediationExecutor === "function"
      ? remediationExecutor
      : createCliRemediationExecutor(cwd);
    const healer = new HealerEngine({ cwd, remediationLimit, taskSlug });
    result = await healer.run({ initialResult: result, validate: validateWorkflow, remediate: executor });
  }

  if (isTerminalFailure(result.overallStatus)) {
    throw new Error(`[WORKFLOW BLOCKED] ${result.overallStatus}: ${result.reason || "Validation could not be resolved safely."}`);
  }
  if (isRecoverableGateFailure(result.overallStatus)) {
    throw new Error(`[WORKFLOW BLOCKED] Unresolved gate status after remediation: ${result.overallStatus}`);
  }

  console.log("\n--- Final Handoff ---");
  const handoffEngine = new HandoffEngine({ cwd });
  const handoffPath = await handoffEngine.generate({
    taskId: path.basename(specPath, ".md"),
    status: result.overallStatus,
    specPaths: [specPath],
    evidence: result.evidence,
    nextActions: `Profile ${workflowProfile}. Observed validation completed. Ready for review or explicitly approved release actions.`
  });

  console.log(`\n[AI WORKFLOW COMPLETE] ${result.overallStatus}`);
  if (result.remediation?.attempts) console.log(`Remediation attempts: ${result.remediation.attempts}`);
  console.log(`Handoff Packet: ${path.relative(cwd, handoffPath)}\n`);
  return { ...result, workflowProfile };
}
